import React, { useRef, useState, useLayoutEffect } from "react";
import { TagClearV2 } from "oolib";
import { StyledChipsInner, StyledChipsOuter } from "./styled";
import { Tag } from "oolib/dist/v2/components/Tags";

const TWO_LINE_HEIGHT = 56;

export const FilterValues = ({ values, onRemove, noAppliedValueChipsTruncation = false }) => {
  const innerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(null); // null = measuring
  const [expanded, setExpanded] = useState(false);

  // Reset measurement when values change
  useLayoutEffect(() => {
    if (noAppliedValueChipsTruncation || expanded) return;
    setVisibleCount(null);
  }, [values.length, expanded, noAppliedValueChipsTruncation]);

  // Measure how many chips fit within TWO_LINE_HEIGHT
  useLayoutEffect(() => {
    if (noAppliedValueChipsTruncation || expanded || visibleCount !== null) return;

    const el = innerRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      if (!el) return;
      const containerTop = el.getBoundingClientRect().top;
      const children = el.children;
      let count = 0;
      for (let i = 0; i < children.length; i++) {
        const childRect = children[i].getBoundingClientRect();
        const relativeTop = childRect.top - containerTop;
        if (relativeTop + childRect.height <= TWO_LINE_HEIGHT) {
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    });
  }, [visibleCount, expanded, noAppliedValueChipsTruncation]);

  // noAppliedValueChipsTruncation short-circuits the entire measurement + "+N" overflow
  // affordance — render all chips, ignore TWO_LINE_HEIGHT entirely. Used for
  // the color-code breakdown picker where every selected chip needs to be
  // visible at a glance.
  const isMeasuring = !noAppliedValueChipsTruncation && visibleCount === null && !expanded;
  const hasOverflow = !noAppliedValueChipsTruncation && !expanded && visibleCount !== null && visibleCount < values.length;
  const hiddenCount = hasOverflow ? values.length - visibleCount : 0;
  const displayValues = noAppliedValueChipsTruncation || expanded || isMeasuring ? values : values.slice(0, visibleCount);

  return (
    <StyledChipsOuter>
      <StyledChipsInner
        ref={innerRef}
        $expanded={expanded || !isMeasuring}
      >
        {displayValues.map((v) => (
          <TagClearV2
            key={v.value}
            id={v.value}
            display={v.display}
            size="XS"
            // `badgeColor` is set by callers that want chip colors (e.g.
            // color-code breakdown selections); undefined elsewhere so chips
            // render plain.
            badgeColor={v.badgeColor}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(v);
            }}
          />
        ))}

        {hasOverflow && (
          <Tag 
            display={`+ ${hiddenCount}`} 
            size="XS" 
            onClick={() => setExpanded(true)}
          />
        )}
      </StyledChipsInner>

      {expanded && visibleCount !== null && visibleCount < values.length && (
        <Tag 
          display={`Show less`} 
          size="XS" 
          onClick={() => setExpanded(false)} 
          style={{ marginTop: "0.3rem" }}
        />
      )}
    </StyledChipsOuter>
  );
};
